import { memo, useContext, useMemo, useState, useEffect } from 'react';
import { Position } from '@xyflow/react';
import { Text, Divider, Spacer, useTheme } from '@Sage/hoi-poi-ui';
import dot from 'utils/dot';

import { getAutomationVariables } from 'services/AutomationsService';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { isEmptyObject } from 'utils/objects';
import { errorToast } from 'utils/toast';

import {
    findNodeByType,
    AUTOMATION_ENTITIES,
    ACTION_ICONS,
    ACTION_OPERATORS,
    ACTION_OPERATORS_NO_INPUT,
    buildPathTree,
} from '../../../../utils';

import { AutomationBuilderCanvasContext } from '../../AutomationBuilderCanvasContext';
import AutomationBuilderBox from '../../components/AutomationBuilderBox';
import AutomationCanvasHandle from '../../components/AutomationCanvasHandle';
import ActionBoxValue from './ActionBoxValue';

const AutomationBuilderActionBox = memo((props) => {
    const theme = useTheme();
    const { data, getActions } = useContext(AutomationBuilderCanvasContext);
    const [actions, setActions] = useState([]);
    const nodeData = useMemo(() => findNodeByType(data?.scene, props.id), [data?.scene, props.id]);
    const [entityFieldsMap, setEntityFieldsMap] = useState();

    useEffect(() => {
        getActions()
            .then((result) => {
                setActions(result);
            })
            .catch((e) => {
                console.error(e);
                errorToast({ text: getLiteral('error_generalerror') });
            });
    }, [getActions]);

    useEffect(() => {
        const nodePath = dot.findPathByAttr(data, 'id', props.id);
        if (!nodePath) return;
        const nodeBranch = buildPathTree(data, nodePath);
        getAutomationVariables(nodeBranch?.scene)
            .then((result) => {
                const newEntityFieldsMap = {};

                result?.values?.variables?.entity?.sections?.forEach((section) => {
                    section.fields.map((field) => {
                        const key = field?.value?.replace(/{{\s*|\s*}}/g, '')?.trim();
                        newEntityFieldsMap[key] = {
                            ...field,
                            label: `${getLiteral(field.literal)} (${getLiteralWithParameters(
                                'label_automation_entity_trigger',
                                [getLiteral(data?.scene?.parameters?.entity?.description)],
                            )})`,
                            icon: result?.values?.variables?.entity?.entity,
                        };
                    });
                });

                result?.values?.variables?.related?.forEach((entity) => {
                    entity.sections?.forEach((section) =>
                        section.fields?.forEach((field) => {
                            const key = field?.value?.replace(/{{\s*|\s*}}/g, '')?.trim();
                            newEntityFieldsMap[key] = {
                                ...field,
                                label: `${getLiteral(field.literal)} (${getLiteral(entity.description)})`,
                                icon: entity.entity,
                            };
                        }),
                    );
                });

                setEntityFieldsMap(newEntityFieldsMap);
            })
            .catch((e) => {
                console.error(e);
                errorToast({ text: getLiteral('error_generalerror') });
            });
    }, [data, props.id]);

    const content = useMemo(() => {
        if (!actions?.length || !entityFieldsMap) return;

        const entity = AUTOMATION_ENTITIES.find(
            (entity) => entity.trueName === nodeData?.parameters?.entity?.entity,
        );

        if (!entity) return;

        const action = actions?.find((a) => a.value === nodeData?.name);

        return (
            <>
                <Text type="subtitle" bold>
                    {`${action.label} ${getLiteral(entity?.labels?.singular)?.toLowerCase()}: ${getLiteralWithParameters(
                        nodeData?.parameters?.entity?.literal,
                        [getLiteral(data?.scene?.parameters?.entity?.description)],
                    )}`}
                </Text>
                <Spacer y={2} />
                <Divider />
                <Spacer y={2} />
                {nodeData?.parameters?.values.map((value) => (
                    <div
                        key={value.field.literal}
                        className="fm-automation-builder__action-box__field"
                    >
                        <Text type="subtitle" bold>
                            {getLiteral(value.field.literal)}
                        </Text>
                        <Spacer y={1} />
                        <div className="fm-automation-builder__action-box__field-row">
                            <Text color={theme.colors.textLight.secondary}>
                                {getLiteral(ACTION_OPERATORS[value?.value?.type])}
                                {ACTION_OPERATORS_NO_INPUT.includes(value?.value?.type) ? '' : ':'}
                            </Text>
                            <ActionBoxValue item={value} entityFieldsMap={entityFieldsMap} />
                        </div>
                        <Spacer y={2} />
                    </div>
                ))}
            </>
        );
    }, [
        actions,
        data?.scene?.parameters?.entity?.description,
        entityFieldsMap,
        nodeData?.name,
        nodeData?.parameters?.entity?.entity,
        nodeData?.parameters?.entity?.literal,
        nodeData?.parameters?.values,
        theme.colors.textLight.secondary,
    ]);

    return (
        <>
            <AutomationCanvasHandle
                className="fm-automation-builder__handler-action"
                type="target"
                position={Position.Top}
                id="top"
                boxId={props.id}
                isIncomplete={nodeData?._isIncomplete}
            />
            <AutomationBuilderBox
                {...props}
                key={nodeData?.id}
                className="fm-automation-builder__action-box"
                title={`${getLiteral('label_action')} ${nodeData?.position}`}
                icon={
                    nodeData?.parameters?.entity?.entity
                        ? nodeData?.parameters?.entity?.entity
                        : 'automations'
                }
                secondIcon={
                    !isEmptyObject(nodeData?.parameters) ? ACTION_ICONS[nodeData?.name] : ''
                }
                emptyPlaceholder={getLiteral('action_automation_add_action')}
                isEnd={!nodeData?.next?.length}
                isIncomplete={nodeData?._isIncomplete}
                allowDelete
            >
                {content}
            </AutomationBuilderBox>
            <AutomationCanvasHandle
                className="fm-automation-builder__handler-action"
                type="source"
                position={Position.Bottom}
                id="bottom"
                boxId={props.id}
                isIncomplete={nodeData?._isIncomplete}
            />
        </>
    );
});

export default AutomationBuilderActionBox;
